import { Stack, Text } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import type { ReactElement } from 'react'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { getSessionUrl } from '@/client/services/session'
import * as url from '@/client/services/url'

import ShwLink from '@/components/ShwLink'
import ModalV3 from '@/components/UI/Modal/ModalV3'

import JoinSessionButton from './JoinSessionButton'
import type { ListItemWithEmphasis } from './types'
import UnorderedListWithEmphasis from './UnorderedListWithEmphasis'

const TUTOR_START_LIST_ITEMS: ListItemWithEmphasis[] = [
  {
    emphasizedText: 'Introduce',
    normalText: 'yourself and have learners introduce themselves.',
  },
  {
    emphasizedText: 'Ensure',
    normalText: 'that every learner participates once through audio, video, or chat.',
  },
]

export default function TutorStartSessionModal({
  open,
  onClose,
  sessionId,
  seriesId,
  topicName,
}: {
  open: boolean
  onClose: () => void
  sessionId: number
  seriesId: number | null
  topicName: string
}): ReactElement {
  const router = useRouter()

  const sessionOrSeriesUrl = getSessionUrl({ sessionId, seriesId, topicName })
  const worldsSessionOrSeriesUrl = url.getSessionOrSeriesUrl({ sessionId, seriesId })

  const shouldRedirect =
    !url.helpers.routerMatchesUrl(router, sessionOrSeriesUrl) &&
    !url.helpers.routerMatchesUrl(router, worldsSessionOrSeriesUrl)

  const [handleJoinSession, isJoiningSession] = useAPIWrapper(
    async () => {
      onClose()
      if (shouldRedirect) {
        await router.push(sessionOrSeriesUrl)
      }
    },
    [onClose, router, sessionOrSeriesUrl, shouldRedirect],
    'Tutor Join Session'
  )

  return (
    <ModalV3
      size="xl"
      open={open}
      onClose={onClose}
      title="Start Session"
      body={() => (
        <Stack direction="column" spacing="8px">
          <Text textAlign="left">When you start your session, please remember to:</Text>
          <UnorderedListWithEmphasis listItems={TUTOR_START_LIST_ITEMS} />
          <Text>
            Be sure to report any inappropriate behavior on the{' '}
            <ShwLink href={url.getReportUrl()}>feedback form</ShwLink>. And of course, have fun!
          </Text>
        </Stack>
      )}
      rightFooterContent={() => (
        <JoinSessionButton
          isHost
          sessionId={sessionId}
          topicName={topicName}
          isDisabled={isJoiningSession}
          onClick={handleJoinSession}
        />
      )}
    />
  )
}
